/*
 *  Copyright IBM Corp. 2025, 2026
 *
 *  This source code is licensed under the Apache-2.0 license found in the
 *  LICENSE file in the root directory of this source tree.
 *
 *  @license
 */

import type { FileUpload } from '../config/ServiceDeskConfig';
import type { StructuredData } from '../messaging/Messages';
import type { FileUploadCapabilities } from '../instance/FileUploadCapabilities';
import type { JSONContent } from '@tiptap/core';

/**
 * The status of a single pending file upload.
 *
 * - `UPLOADING` — the upload is in progress; the send button is disabled.
 * - `COMPLETE` — the upload finished successfully; `contributedData` is populated.
 * - `ERROR` — the upload failed; `errorMessage` is populated.
 *
 * @internal
 */
export enum PendingUploadStatus {
  UPLOADING = 'uploading',
  COMPLETE = 'complete',
  ERROR = 'error',
}

/**
 * State for a single file that is being (or has been) uploaded via
 * {@link UploadConfig.onFileUpload}. The chat widget tracks one of these per
 * in-flight or completed upload and uses them to build the
 * `pendingStructuredData` attached to the outgoing message.
 *
 * @internal
 */
export interface PendingUpload {
  /**
   * Stable unique identifier for this upload, generated by the widget.
   */
  id: string;

  /**
   * The original `File` object selected by the user.
   */
  file: File;

  /**
   * Current lifecycle status of the upload.
   */
  status: PendingUploadStatus;

  /**
   * The `StructuredData` returned by `onFileUpload` once the upload completes.
   * Only populated when `status === "complete"`.
   */
  contributedData?: StructuredData;

  /**
   * Human-readable error message when `status === "error"`.
   */
  errorMessage?: string;
}

/**
 * The state of the stop-streaming button that cancels in-progress response streams.
 */
interface StopStreamingButtonState {
  /**
   * Determines if the button should be visible.
   */
  isVisible: boolean;

  /**
   * Determine if the button should be disabled.
   */
  isDisabled: boolean;

  /**
   * The stream id of the current response with an active stream. It is used by message service to stop streamed
   * responses coming from wxa.
   */
  currentStreamID?: string;
}

/**
 * The state of the input area where the user types messages.
 */
interface InputState extends FileUploadCapabilities {
  /**
   * The canonical raw text value currently inside the input field.
   * ProseMirror's document is the internal source of truth; this is the
   * serialized output.
   */
  rawValue: string;

  /**
   * Tiptap-native `JSONContent` projection of the editor doc — kept in
   * lockstep with `rawValue` (both update from the same input-change
   * event).
   *
   * @experimental
   */
  content: JSONContent;

  /**
   * Whether the input editor currently has focus. Mirrors the
   * `cds-aichat-input-focus` / `cds-aichat-input-blur` events emitted by
   * the input web component.
   *
   * @experimental
   */
  focused: boolean;

  /**
   * Runtime override for input-field visibility. `null` means "no override":
   * the effective value comes from `PublicConfig.input.isVisible` for the
   * assistant input (read via `selectInputFieldVisible`), or defaults to
   * visible for the human-agent input. A non-null value (set by the deprecated
   * `instance.updateInputFieldVisibility`, or by the human-agent flow on its own
   * input slice) wins over config.
   */
  fieldVisible: boolean | null;

  /**
   * Runtime override for the whole-chat read-only state. `null` means "no
   * override": the effective value comes from `PublicConfig.isReadonly` for the
   * assistant input (read via `selectInputIsReadonly`). The human-agent flow
   * sets this on its own input slice to lock input while disconnected.
   */
  isReadonly: boolean | null;

  /**
   * The current set of file attachments selected to be uploaded.
   */
  files: FileUpload[];

  /**
   * The state of the stop streaming button to cancel streams from message responses.
   */
  stopStreamingButtonState: StopStreamingButtonState;

  /**
   * Structured data set directly by the host page via
   * {@link ChatInstanceInput.updateStructuredData}.  This is the "manual" portion of
   * `pendingStructuredData` and is kept separate so that upload contributions can be
   * merged on top without overwriting host-page data.
   */
  manualStructuredData?: StructuredData;

  /**
   * In-flight and completed file uploads initiated via {@link UploadConfig.onFileUpload}.
   * Each entry tracks the lifecycle of one file.  The widget rebuilds
   * `pendingStructuredData` whenever this array changes.
   */
  pendingUploads: PendingUpload[];

  /**
   * The merged result of {@link InputState.manualStructuredData} and the
   * `contributedData` from every completed entry in {@link InputState.pendingUploads}.
   * This is the value that gets attached to the outgoing `MessageRequest` on send.
   *
   * Rebuilt automatically by the reducer whenever `manualStructuredData` or
   * `pendingUploads` changes.
   */
  pendingStructuredData?: StructuredData;
}

export type { InputState, StopStreamingButtonState };
